import { Badge, Box, Circle, SystemStyleObject } from '@chakra-ui/react'
import { WorksheetStatus as GqlWorksheetStatus } from 'src/lib/gql-client'
import { assertNever } from 'src/utils/assertNever'

import { WorksheetStatus } from '../../types'

type Props = {
  status: WorksheetStatus | GqlWorksheetStatus
  isDisabled: boolean
  detailed?: boolean
  hasIncompleteField?: boolean
}

type ColorVariation = 'blue' | 'green' | 'yellow' | 'gray' | 'red' | 'purple'

const colorMap: Record<ColorVariation, { bg: number; text: number }> = {
  blue: { bg: 200, text: 700 },
  green: { bg: 200, text: 700 },
  yellow: { bg: 200, text: 700 },
  gray: { bg: 100, text: 400 },
  red: { bg: 200, text: 700 },
  purple: { bg: 200, text: 700 },
}

const style = (c: ColorVariation, isDisabled: boolean): SystemStyleObject => {
  const { bg: bgNumber, text: textNumber } = colorMap[c]
  return {
    opacity: isDisabled ? 0.3 : 1,
    minW: '44px',
    h: '18px',
    display: 'grid',
    alignContent: 'center',
    fontSize: '10px',
    lineHeight: '18px',
    textAlign: 'center',
    py: '2px',
    color: `${c}.${textNumber}`,
    backgroundColor: `${c}.${bgNumber}`,
  }
}

export const WorksheetStatusBadge: React.FC<Props> = ({
  status,
  isDisabled,
  detailed = false,
  hasIncompleteField = false,
}) => {
  return (
    <Box pos="relative">
      {hasIncompleteField && (
        <Circle
          pos="absolute"
          top="-0.5"
          right="-0.5"
          size="1.5"
          bg="orange.500"
        />
      )}
      <BadgeComponent
        status={status}
        detailed={detailed}
        isDisabled={isDisabled}
      />
    </Box>
  )
}

const BadgeComponent: React.FC<Props> = ({ status, detailed, isDisabled }) => {
  switch (status) {
    case 'created':
    case 'Created':
    case 'deCreated':
    case 'DECreated':
      return <Badge sx={style('blue', isDisabled)}>新規</Badge>
    case 'saved':
    case 'Saved':
      return detailed ? (
        <Badge sx={style('yellow', isDisabled)}>一時保存</Badge>
      ) : (
        <Badge sx={style('yellow', isDisabled)}>要対応</Badge>
      )
    case 'confirmed':
    case 'Confirmed':
      return detailed ? (
        <Badge sx={style('green', isDisabled)}>確定</Badge>
      ) : (
        <Badge sx={style('green', isDisabled)}>完了</Badge>
      )
    case 'disabled':
    case 'Disabled':
    case 'deDisabled':
    case 'DEDisabled':
      return <Badge sx={style('gray', isDisabled)}>無効</Badge>
    case 'dePresend':
    case 'DEPresend':
      return <Badge sx={style('yellow', isDisabled)}>送付前</Badge>
    case 'deAwaitingReceipt':
    case 'DEAwaitingReceipt':
      return <Badge sx={style('red', isDisabled)}>受領待ち</Badge>
    case 'deReceived':
    case 'DEReceived':
      return <Badge sx={style('green', isDisabled)}>受領済</Badge>
    case 'deReject':
    case 'DEReject':
      return <Badge sx={style('purple', isDisabled)}>受領取り下げ</Badge>
    default:
      return assertNever(status)
  }
}
